import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  SipNumberFormat,
  SipTransportProtocol,
} from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import type { SipRegistrationForm } from "./types";

interface SipRegistrationFieldsProps {
  mode: "create" | "edit";
  value: SipRegistrationForm;
  onChange: (changes: Partial<SipRegistrationForm>) => void;
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
}

/**
 * The extension HeyUp registers as on the client's own PBX — what they would
 * type into a desk phone. The PBX then sends us calls by its own rules.
 * Checked again server-side (`connectors/pbx-registration.ts`), which refuses
 * anything that does not fit a SIP URI rather than escaping it.
 */
export const SipRegistrationFields = ({
  mode,
  value,
  onChange,
  phoneNumber,
  onPhoneNumberChange,
}: SipRegistrationFieldsProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-4 rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">
        {t(
          "connectors.sip.intro",
          "Créez un poste pour HeyUp sur votre standard (IPBX), puis saisissez ses identifiants : HeyUp s'y connecte comme un téléphone, et votre standard lui envoie les appels selon vos propres règles.",
        )}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem]">
        <div className="space-y-2">
          <Label htmlFor="sip-server">
            {t("connectors.sip.server", "Adresse du standard")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="sip-server"
            value={value.server}
            onChange={(e) => onChange({ server: e.target.value })}
            placeholder="pbx.exemple.fr"
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sip-port">{t("connectors.sip.port", "Port")}</Label>
          <Input
            id="sip-port"
            inputMode="numeric"
            value={value.port}
            onChange={(e) => onChange({ port: e.target.value })}
            placeholder={value.transport === "tls" ? "5061" : "5060"}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sip-transport">
          {t("connectors.sip.transport", "Transport")}
        </Label>
        <Select
          value={value.transport}
          onValueChange={(transport) =>
            onChange({ transport: transport as SipTransportProtocol })
          }
        >
          <SelectTrigger id="sip-transport">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="udp">UDP</SelectItem>
            <SelectItem value="tcp">TCP</SelectItem>
            <SelectItem value="tls">TLS</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="sip-username">
            {t("connectors.sip.username", "Numéro de poste")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="sip-username"
            value={value.username}
            onChange={(e) => onChange({ username: e.target.value })}
            placeholder="120"
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sip-auth-username">
            {t("connectors.sip.authUsername", "Identifiant d'authentification")}
          </Label>
          <Input
            id="sip-auth-username"
            value={value.authUsername}
            onChange={(e) => onChange({ authUsername: e.target.value })}
            placeholder={t(
              "connectors.sip.authUsernamePlaceholder",
              "Si différent du poste",
            )}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sip-password">
          {t("connectors.sip.password", "Mot de passe du poste")}
          {mode === "create" && <span className="text-red-500"> *</span>}
        </Label>
        <Input
          id="sip-password"
          type="password"
          value={value.password}
          onChange={(e) => onChange({ password: e.target.value })}
          placeholder={
            mode === "edit"
              ? t(
                  "connectors.sip.passwordKeep",
                  "Laisser vide pour conserver le mot de passe actuel",
                )
              : ""
          }
          autoComplete="new-password"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[7rem_1fr]">
        <div className="space-y-2">
          <Label htmlFor="sip-outbound-prefix">
            {t("connectors.sip.outboundPrefix", "Préfixe de sortie")}
          </Label>
          <Input
            id="sip-outbound-prefix"
            inputMode="numeric"
            value={value.outboundPrefix}
            onChange={(e) => onChange({ outboundPrefix: e.target.value })}
            placeholder="0"
            disabled={value.numberFormat === "e164"}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sip-number-format">
            {t("connectors.sip.numberFormat", "Format des numéros")}
          </Label>
          <Select
            value={value.numberFormat}
            onValueChange={(numberFormat) =>
              onChange({
                numberFormat: numberFormat as SipNumberFormat,
                // A prefix only goes with national numbering.
                ...(numberFormat === "e164" ? { outboundPrefix: "" } : {}),
              })
            }
          >
            <SelectTrigger id="sip-number-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="national">
                {t("connectors.sip.numberFormatNational", "National (06…)")}
              </SelectItem>
              <SelectItem value="e164">
                {t("connectors.sip.numberFormatE164", "International (+33…)")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        {t(
          "connectors.sip.outboundHint",
          "Les appels de l'agent (rappels, transferts vers un mobile) sortent par votre standard, avec le numéro qu'il présente pour ce poste. Le préfixe est le chiffre à composer pour obtenir une ligne extérieure, s'il y en a un.",
        )}
      </p>

      <div className="space-y-2">
        <Label htmlFor="sip-phone-number">
          {t("connectors.sip.phoneNumber", "Numéro de la ligne (facultatif)")}
        </Label>
        <Input
          id="sip-phone-number"
          value={phoneNumber}
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          placeholder="+33123456789"
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "connectors.sip.phoneNumberHint",
            "Le numéro que vos clients composent, pour l'affichage. Les appels sont reconnus par le poste, pas par ce numéro.",
          )}
        </p>
      </div>
    </div>
  );
};
